import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { FrmSelectScalarWithHoverHelp } from "components/formFields/FrmSelectScalar";
import { FrmSelectScalarWithObjectOptionsWithHoverHelp } from "components/formFields/FrmSelectScalarWithObjectOptions";
import { FrmTextFieldWithHoverHelp } from "components/formFields/FrmTextFieldWithHoverHelp";
import { FrmAccount } from "components/formFields/frm/FrmAccount";
import { FrmChannel } from "components/formFields/frm/FrmChannel";
import { FrmCheckboxWithHoverHelp } from "components/formFields/frm/FrmCheckboxWithHoverHelp";
import { FrmClass } from "components/formFields/frm/FrmClass";
import { FrmDepartment } from "components/formFields/frm/FrmDepartment";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { HoverHelp } from "components/utility/HoverHelp";
import { Loading } from "components/utility/Loading";

import { i18n } from "services/i18nService";
import {
  CONNECTION_TYPE_OPTIONS,
  CONNECTOR_APP_ID,
  TRANSACTION_TYPE_OPTIONS,
} from "services/sosInventoryService/cart/schema";
import { getRecord } from "services/sosInventoryService/sosApi";
import { setPageDirty } from "services/utility/edit";
import { isPersistedRecord } from "services/utility/misc";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { BigCommerceConnectionFields } from "views/Carts/Cart/BigCommerceConnectionFields";
import { Instructions } from "views/Carts/Cart/Instructions";
import { PayPalPaymentFields } from "views/Carts/Cart/PayPalPaymentFields";
import { ShopifyConnectionFields } from "views/Carts/Cart/ShopifyConnectionFields";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function Cart(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [id, dispatch, setRecord, objectType]);

  // connectionType is only editable on new (unsaved) carts — the field is
  // disabled for persisted records. When switching to Shopify, we clear
  // "enabled" because Shopify Connector carts must complete the OAuth flow
  // before data sync can be permitted; allowing an enabled cart without an
  // established connection would cause sync errors.
  function handleConnectionTypeChange(_, value) {
    setPageDirty();
    setRecord((prevRecord) => ({
      ...prevRecord,
      connectionType: value,
      ...(value === "Shopify" && prevRecord?.enabled ? { enabled: false } : {}),
    }));
  }

  const isPayPal = record?.connectionType === "PayPal";
  const isShopify = record?.connectionType === "Shopify";
  const isBigCommerce = record?.connectionType === "BigCommerce";
  const isConnectorNotConnected =
    isShopify &&
    record?.shopifyAppType === CONNECTOR_APP_ID &&
    !record?.authorizationField1;

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        setReloadRecord={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "min-content 1fr",
                      columnGap: "60px",
                      alignItems: "start",
                    }}
                  >
                    <div
                      style={{
                        gridColumn: "1 / 2",
                        maxWidth: "20rem",
                        minWidth: "20rem",
                        gap: "10px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <FrmSelectScalarWithHoverHelp
                        selectProps={{
                          name: "connectionType",
                          label: i18n("frmLabel.Cart"),
                          value: record.connectionType,
                          onValueChange: handleConnectionTypeChange,
                          options: CONNECTION_TYPE_OPTIONS,
                          disabled: isPersistedRecord(record.id),
                          fullWidth: true,
                        }}
                        helpText={i18n("hoverHelp.cart.connectionType")}
                      />
                      <FrmTextFieldWithHoverHelp
                        name="name"
                        value={record.name}
                        label={i18n("frmLabel.Name")}
                        onValueBlur={handleInputFieldChange}
                        helpText={i18n("hoverHelp.cart.name")}
                        error={isInError("name")}
                        minWidth="20rem"
                      />
                      <FrmResizeTextField
                        name="description"
                        value={record.description}
                        label={i18n("frmLabel.Description")}
                        minRows={2}
                        width="100%"
                        onValueBlur={handleInputFieldChange}
                      />
                      <FrmCheckboxWithHoverHelp
                        name="enabled"
                        value={record.enabled}
                        onValueChange={handleInputFieldChange}
                        label={i18n("frmLabel.Enabled")}
                        helpText={i18n("hoverHelp.cart.enabled")}
                        disabled={isConnectorNotConnected}
                      />
                      {isShopify ? (
                        <ShopifyConnectionFields
                          record={record}
                          objectType={objectType}
                          handleInputFieldChange={handleInputFieldChange}
                          isInError={isInError}
                          setRecord={setRecord}
                          setErrors={setErrors}
                        />
                      ) : isBigCommerce ? (
                        <BigCommerceConnectionFields
                          record={record}
                          handleInputFieldChange={handleInputFieldChange}
                          isInError={isInError}
                        />
                      ) : isPayPal ? (
                        <FrmTextFieldWithHoverHelp
                          name="storeIdentifier"
                          value={record.storeIdentifier}
                          label={i18n("frmLabel.PayPalPaymentAddress")}
                          onValueBlur={handleInputFieldChange}
                          helpText={i18n("hoverHelp.cart.followInstructions")}
                          error={isInError("storeIdentifier")}
                        />
                      ) : null}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmLocation
                          fullWidth
                          value={record.location}
                          onValueChange={handleInputFieldChange}
                          error={isInError("location")}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.location")} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmChannel
                          fullWidth
                          value={record.channel}
                          onValueChange={handleInputFieldChange}
                          label={i18n("frmLabel.SalesChannel")}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.channel")} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmClass
                          value={record.class}
                          onValueChange={handleInputFieldChange}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.class")} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FrmDepartment
                          value={record.department}
                          onValueChange={handleInputFieldChange}
                        />
                        <HoverHelp title={i18n("hoverHelp.cart.department")} />
                      </div>
                      <FrmSelectScalarWithObjectOptionsWithHoverHelp
                        selectProps={{
                          fullWidth: true,
                          name: "txnType",
                          options: TRANSACTION_TYPE_OPTIONS,
                          label: i18n("frmLabel.TransactionType"),
                          onValueChange: handleInputFieldChange,
                          value: record.txnType,
                          error: isInError("txnType"),
                        }}
                        helpText={i18n("hoverHelp.cart.txnType")}
                      />
                      <FrmAccount
                        name="depositAccount"
                        label={i18n("frmLabel.SalesReceiptDepositAccount")}
                        onValueChange={handleInputFieldChange}
                        value={record.depositAccount}
                        accountType="deposit"
                        helpText={i18n("cart.depositAccount.hoverHelp")}
                      />
                      {isPayPal && (
                        <PayPalPaymentFields
                          record={record}
                          handleInputFieldChange={handleInputFieldChange}
                        />
                      )}
                      {!isPayPal && (
                        <>
                          <FrmCheckboxWithHoverHelp
                            name="useSosDescription"
                            value={record.useSosDescription}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.OverrideDescription")}
                            helpText={i18n("hoverHelp.cart.useSosDescription")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="useSosOrderNumber"
                            value={record.useSosOrderNumber}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UseSosOrderNumber")}
                            helpText={i18n("hoverHelp.cart.useSosOrderNumber")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="useCommonCustomer"
                            value={record.useCommonCustomer}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UseCommonCustomer")}
                            helpText={i18n("hoverHelp.cart.useCommonCustomer")}
                          />
                          <FrmCheckboxWithHoverHelp
                            name="updateInventory"
                            value={record.updateInventory}
                            onValueChange={handleInputFieldChange}
                            label={i18n("frmLabel.UpdateInventory")}
                            helpText={i18n("hoverHelp.cart.updateInventory")}
                          />
                        </>
                      )}
                    </div>
                    <div style={{ gridColumn: "2 / 2" }}>
                      <Instructions
                        connectionType={record.connectionType}
                        shopifyAppType={record.shopifyAppType}
                      />
                    </div>
                  </div>
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
